import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createAppUser, type AppUserCreateRequest, type AppUserStatus } from '../lib/api';
import { theme } from '../theme';

export function NewAppUserPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<AppUserCreateRequest>({ phoneE164: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: AppUserCreateRequest = {
        ...form,
        phoneE164: form.phoneE164.trim(),
        firstName: form.firstName?.trim() || undefined,
        lastName: form.lastName?.trim() || undefined,
        rooftopName: form.rooftopName?.trim() || undefined,
        associatedTradeBidUser: form.associatedTradeBidUser?.trim() || undefined,
        associatedTramovoUser: form.associatedTramovoUser?.trim() || undefined,
      };
      const res = await createAppUser(payload);
      navigate(`/app-users/${encodeURIComponent(res.user.userId)}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const update = (field: keyof AppUserCreateRequest, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <Link to="/app-users" style={{ fontSize: 13, color: theme.text.secondary }}>
        ← All app users
      </Link>
      <h1 style={{ marginTop: 12 }}>New app user</h1>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        Pre-approves a mobile user. They'll be able to sign in immediately on the mobile app using
        this phone number — no email or password required.
      </p>
      <form
        onSubmit={(e) => void submit(e)}
        style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, maxWidth: 640 }}
      >
        <label>Phone (E.164) *</label>
        <input
          value={form.phoneE164}
          onChange={(e) => update('phoneE164', e.target.value)}
          placeholder="+353871234567"
          required
        />
        <label>Status</label>
        <select
          value={form.status}
          onChange={(e) => update('status', e.target.value as AppUserStatus)}
        >
          <option value="active">Active (can upload immediately)</option>
          <option value="pending-approval">Pending approval</option>
          <option value="disabled">Disabled</option>
        </select>
        <label>First name</label>
        <input value={form.firstName ?? ''} onChange={(e) => update('firstName', e.target.value)} />
        <label>Last name</label>
        <input value={form.lastName ?? ''} onChange={(e) => update('lastName', e.target.value)} />
        <label>Company</label>
        <input
          value={form.rooftopName ?? ''}
          onChange={(e) => update('rooftopName', e.target.value)}
          placeholder="e.g. Dublin Branch"
        />
        <label>TradeBid user</label>
        <input
          value={form.associatedTradeBidUser ?? ''}
          onChange={(e) => update('associatedTradeBidUser', e.target.value)}
        />
        <label>Tramovo user</label>
        <input
          value={form.associatedTramovoUser ?? ''}
          onChange={(e) => update('associatedTramovoUser', e.target.value)}
        />
        <div />
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="submit"
            disabled={saving || !form.phoneE164}
            style={{
              padding: '8px 16px',
              background: theme.brand.primary,
              color: theme.text.onBrand,
              border: 'none',
              borderRadius: theme.radius.md,
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            {saving ? 'Creating…' : 'Create user'}
          </button>
          <Link
            to="/app-users"
            style={{
              padding: '8px 16px',
              background: theme.surface.panel,
              color: theme.text.primary,
              border: `1px solid ${theme.surface.borderStrong}`,
              borderRadius: theme.radius.md,
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            Cancel
          </Link>
        </div>
      </form>
      {error && <p style={{ color: theme.status.error, marginTop: 16 }}>Error: {error}</p>}
    </div>
  );
}
